import React, { useState, useEffect, useRef } from 'react';
import { Shield, FileText, ChevronRight, Download, ArrowUp, CheckCircle, AlertTriangle, Users, Database, Lock, Scale, Calendar, Mail, Eye, Share2 } from 'lucide-react';

import Header from '../component/layout/Header';
import Footer from '../component/layout/Footer';

const PrivacyPolicyPage = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [activeSection, setActiveSection] = useState('');
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Handle scroll for show scroll-to-top button and active section
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 500);

            // Update active section based on scroll position
            const sections = document.querySelectorAll('[data-section]');
            let current = '';

            sections.forEach((section) => {
                const sectionTop = section.offsetTop;
                const sectionHeight = section.clientHeight;
                if (window.scrollY >= sectionTop - 200) {
                    current = section.getAttribute('data-section');
                }
            });

            setActiveSection(current);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const scrollToSection = (sectionId) => {
        const element = document.querySelector(`[data-section="${sectionId}"]`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const sections = [
        { id: 'introduction', title: 'Introduction', icon: Shield },
        { id: 'definitions', title: 'Definitions', icon: FileText },
        { id: 'data-collection', title: 'What Data We Collect', icon: Database },
        { id: 'when-how', title: 'When & How We Collect', icon: Calendar },
        { id: 'why-collect', title: 'Why We Collect Data', icon: Eye },
        { id: 'data-sharing', title: 'Data Sharing', icon: Share2 },
        { id: 'retention', title: 'Data Retention', icon: Lock },
        { id: 'security', title: 'Data Security', icon: Shield },
        { id: 'children', title: 'Children', icon: Users },
        { id: 'your-rights', title: 'Your Rights', icon: CheckCircle },
        { id: 'changes', title: 'Policy Changes', icon: AlertTriangle },
        { id: 'contact', title: 'Contact Us', icon: Mail }
    ];

    const SectionCard = ({ section, children, delay = 0 }) => (
        <div
            data-section={section.id}
            className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
            style={{ transitionDelay: `${delay}s` }}
        >
            <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-[#F05A28] rounded-xl flex items-center justify-center mr-4">
                    <section.icon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                {children}
            </div>
        </div>
    );

    return (
        <>
            <Header />
            <div ref={sectionRef} className="min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-gradient-to-br from-[#F05A28] to-orange-600 text-white py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                        >
                            <div className="flex items-center mb-6">
                                <Shield className="w-8 h-8 mr-4" />
                                <span className="text-orange-100 font-semibold tracking-wider text-sm uppercase">
                                    Legal Information
                                </span>
                            </div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                                Privacy Policy
                            </h1>
                            <p className="text-xl text-orange-100 leading-relaxed max-w-3xl">
                                We take your privacy seriously. Learn how we collect, use, and protect your personal information.
                            </p>
                            <div className="mt-8 flex items-center text-orange-100">
                                <Calendar className="w-5 h-5 mr-2" />
                                <span>Last updated: January 10, 2024</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid lg:grid-cols-12 gap-12">

                        {/* Table of Contents - Sidebar */}
                        <div className="lg:col-span-3">
                            <div className="sticky top-8">
                                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-900 mb-6">Contents</h3>
                                    <nav className="space-y-2">
                                        {sections.map((section, index) => (
                                            <button
                                                key={section.id}
                                                onClick={() => scrollToSection(section.id)}
                                                className={`w-full flex items-center text-left p-3 rounded-xl transition-all duration-300 ${activeSection === section.id
                                                    ? 'bg-[#F05A28] text-white'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <section.icon className="w-4 h-4 mr-3 flex-shrink-0" />
                                                <span className="text-sm font-medium">{section.title}</span>
                                                <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0" />
                                            </button>
                                        ))}
                                    </nav>
                                </div>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="lg:col-span-9">

                            {/* Introduction */}
                            <SectionCard section={sections[0]} delay={0.2}>
                                <p className="mb-4">
                                    We at RoadExpress Technology Private Limited and our affiliates (hereinafter referred to as "RoadExpress", "we", "us" or "our") consider customer trust as our top priority and take the protection of your personal information very seriously.
                                </p>
                                <p className="mb-4">
                                    We want you to feel safe using our products, services, and solutions ("Services"). This Privacy Policy informs you of the way in which we collect, use, transfer, and store your personal information when you use our Services, as well as your rights in relation to this data.
                                </p>
                                <div className="bg-orange-50 border-l-4 border-[#F05A28] p-4 rounded-r-lg">
                                    <p className="text-orange-800 font-medium">
                                        Please read this Privacy Policy carefully before accessing or availing our Services.
                                    </p>
                                </div>
                            </SectionCard>

                            {/* Definitions */}
                            <SectionCard section={sections[1]} delay={0.3}>
                                <p className="mb-6">In this policy, the below terms shall have the following meanings:</p>
                                <div className="space-y-4">
                                    <div className="border-l-4 border-blue-400 pl-4">
                                        <h4 className="font-semibold text-gray-900 mb-2">"Consignee"</h4>
                                        <p>Shall mean the entity (individual/firm) who is financially responsible (the buyer) for the receipt of a shipment from a Consignor.</p>
                                    </div>
                                    <div className="border-l-4 border-green-400 pl-4">
                                        <h4 className="font-semibold text-gray-900 mb-2">"Consignor"</h4>
                                        <p>Shall mean a person or firm (usually the seller) who avails the Services of RoadExpress to deliver a consignment to a Consignee.</p>
                                    </div>
                                    <div className="border-l-4 border-purple-400 pl-4">
                                        <h4 className="font-semibold text-gray-900 mb-2">"Data Subject"</h4>
                                        <p>Shall mean any person who can be identified, directly or indirectly, via an identifier such as a name, an ID number, contact details, or location data.</p>
                                    </div>
                                    <div className="border-l-4 border-orange-400 pl-4">
                                        <h4 className="font-semibold text-gray-900 mb-2">"Third Party Vendors"</h4>
                                        <p>Shall mean any individual or organisation providing services to RoadExpress on a contractual basis.</p>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* What Data We Collect */}
                            <SectionCard section={sections[2]} delay={0.4}>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
                                <p className="mb-4">
                                    While using our Services, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you. This may include:
                                </p>
                                <div className="grid md:grid-cols-2 gap-4 mb-6">
                                    <div className="bg-gray-50 p-4 rounded-xl">
                                        <ul className="space-y-2">
                                            <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />Email Address</li>
                                            <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />First Name and Last Name</li>
                                            <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />Phone Number</li>
                                            <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />Postal Address</li>
                                        </ul>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-xl">
                                        <ul className="space-y-2">
                                            <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />KYC Details / Documents</li>
                                            <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />Bank Account Information</li>
                                            <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />Geographical Location</li>
                                            <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />Device Information</li>
                                        </ul>
                                    </div>
                                </div>

                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Usage Data</h3>
                                <p className="mb-4">
                                    We may collect information from devices you use to access our applications based on your settings and permissions:
                                </p>
                                <ul className="list-disc list-inside space-y-2 text-gray-700">
                                    <li>Device attributes (operating system, hardware versions, battery level)</li>
                                    <li>Device operations and behaviors</li>
                                    <li>Unique identifiers and device IDs</li>
                                    <li>Network and connection information</li>
                                    <li>Location data (with permission)</li>
                                </ul>
                            </SectionCard>

                            {/* When & How We Collect */}
                            <SectionCard section={sections[3]} delay={0.5}>
                                <p className="mb-4">
                                    We collect Personal Information from you in several ways to provide our range of delivery and logistics solutions:
                                </p>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div className="flex items-start">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 mt-1">
                                                <Users className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">Direct Collection</h4>
                                                <p className="text-gray-600 text-sm">When you use our website, mobile apps, or contact us</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 mt-1">
                                                <CheckCircle className="w-4 h-4 text-green-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">Account Creation</h4>
                                                <p className="text-gray-600 text-sm">When you register for our services</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3 mt-1">
                                                <Database className="w-4 h-4 text-purple-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">Service Usage</h4>
                                                <p className="text-gray-600 text-sm">When you track packages or use our services</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="flex items-start">
                                            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3 mt-1">
                                                <Mail className="w-4 h-4 text-orange-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">Customer Support</h4>
                                                <p className="text-gray-600 text-sm">When you contact our support teams</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3 mt-1">
                                                <Share2 className="w-4 h-4 text-red-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">Social Media</h4>
                                                <p className="text-gray-600 text-sm">Through feedback on our social platforms</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3 mt-1">
                                                <FileText className="w-4 h-4 text-indigo-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">Surveys & Forms</h4>
                                                <p className="text-gray-600 text-sm">Through web forms and customer surveys</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Why We Collect Data */}
                            <SectionCard section={sections[4]} delay={0.6}>
                                <p className="mb-6">
                                    We use the collected data to provide, develop, and improve our services. Our purposes include:
                                </p>
                                <div className="grid gap-6">
                                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                                        <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                                            <Database className="w-5 h-5 mr-2" />
                                            Service Provision
                                        </h4>
                                        <p className="text-blue-800">Provide transportation, warehousing, freight, and technology services with real-time tracking and updates.</p>
                                    </div>
                                    <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                                        <h4 className="font-semibold text-green-900 mb-3 flex items-center">
                                            <Mail className="w-5 h-5 mr-2" />
                                            Communication
                                        </h4>
                                        <p className="text-green-800">Contact you about services, send tracking updates, and provide customer support.</p>
                                    </div>
                                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                                        <h4 className="font-semibold text-purple-900 mb-3 flex items-center">
                                            <Users className="w-5 h-5 mr-2" />
                                            Personalization
                                        </h4>
                                        <p className="text-purple-800">Understand your preferences and customize your experience with tailored services.</p>
                                    </div>
                                    <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200">
                                        <h4 className="font-semibold text-orange-900 mb-3 flex items-center">
                                            <Shield className="w-5 h-5 mr-2" />
                                            Security & Compliance
                                        </h4>
                                        <p className="text-orange-800">Ensure network security, prevent fraud, and comply with legal obligations.</p>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Data Sharing */}
                            <SectionCard section={sections[5]} delay={0.7}>
                                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg mb-6">
                                    <p className="text-red-800 font-medium">
                                        As a rule, we do not transfer or share your Personal Information with any third party unless required.
                                    </p>
                                </div>
                                <p className="mb-6">We may share your Personal Information in these specific circumstances:</p>
                                <div className="space-y-6">
                                    <div className="border rounded-xl p-6">
                                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                            <Users className="w-5 h-5 text-[#F05A28] mr-2" />
                                            Group Companies
                                        </h4>
                                        <p className="text-gray-700">With RoadExpress group companies for service provision and process optimization.</p>
                                    </div>
                                    <div className="border rounded-xl p-6">
                                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                            <Scale className="w-5 h-5 text-[#F05A28] mr-2" />
                                            Government Authorities
                                        </h4>
                                        <p className="text-gray-700">With regulatory authorities when legally required or to protect rights and safety.</p>
                                    </div>
                                    <div className="border rounded-xl p-6">
                                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                            <Database className="w-5 h-5 text-[#F05A28] mr-2" />
                                            Service Providers
                                        </h4>
                                        <p className="text-gray-700">With trusted third-party vendors who assist in service delivery, under strict confidentiality agreements.</p>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Continue with remaining sections... */}
                            <SectionCard section={sections[6]} delay={0.8}>
                                <p className="mb-4">
                                    We retain your Personal Information for as long as needed to fulfill the purpose for which we collected it and for a reasonable period thereafter to comply with:
                                </p>
                                <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                                    <li>Audit and accounting requirements</li>
                                    <li>Contractual obligations</li>
                                    <li>Technical requirements</li>
                                    <li>Legal requirements</li>
                                    <li>Dispute resolution needs</li>
                                </ul>
                                <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl">
                                    <p className="text-blue-800">
                                        <strong>Note:</strong> We may retain non-personal information indefinitely or to the extent allowed by applicable law.
                                    </p>
                                </div>
                            </SectionCard>

                            <SectionCard section={sections[7]} delay={0.9}>
                                <p className="mb-6">
                                    We take information security very seriously and maintain reasonable administrative, technical, and physical safeguards designed to protect your personal information.
                                </p>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                                            <h4 className="font-semibold text-green-900 mb-2">Technical Safeguards</h4>
                                            <p className="text-green-800 text-sm">Encryption, secure servers, and access controls</p>
                                        </div>
                                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                                            <h4 className="font-semibold text-blue-900 mb-2">Administrative Controls</h4>
                                            <p className="text-blue-800 text-sm">Employee training and access limitations</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                                            <h4 className="font-semibold text-purple-900 mb-2">Physical Security</h4>
                                            <p className="text-purple-800 text-sm">Secure facilities and equipment protection</p>
                                        </div>
                                        <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                                            <h4 className="font-semibold text-orange-900 mb-2">Compliance</h4>
                                            <p className="text-orange-800 text-sm">International standard certifications</p>
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>

                            <SectionCard section={sections[8]} delay={1.0}>
                                <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-xl">
                                    <div className="flex items-start">
                                        <AlertTriangle className="w-6 h-6 text-yellow-600 mr-3 mt-1" />
                                        <div>
                                            <h4 className="font-semibold text-yellow-900 mb-2">Age Restriction</h4>
                                            <p className="text-yellow-800 mb-4">
                                                Our Services are not aimed at children (under the age of 18) and do not provide information specifically targeting children.
                                            </p>
                                            <p className="text-yellow-800">
                                                If you are under 18, you may use our Services only with the involvement of a parent or legal guardian.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>

                            <SectionCard section={sections[9]} delay={1.1}>
                                <p className="mb-6">As a data subject, you have the right to enquire about:</p>
                                <div className="grid gap-4">
                                    {[
                                        'What Personal Information we hold about you',
                                        'The purpose of processing such Personal Information',
                                        'The recipients to whom the Personal Information has been disclosed',
                                        'How long we intend to store your Personal Information',
                                        'Information about the source if we didn\'t collect data directly from you',
                                        'The right to have incomplete or inaccurate data corrected',
                                        'The right to request erasure of Personal Information',
                                        'The right to object to direct marketing'
                                    ].map((right, index) => (
                                        <div key={index} className="flex items-start">
                                            <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                                            <p className="text-gray-700">{right}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-6 bg-blue-50 border border-blue-200 p-4 rounded-xl">
                                    <p className="text-blue-800">
                                        <strong>Report Breaches:</strong> You can report any Personal Information breach at connect@roadexpressfreight.com
                                    </p>
                                </div>
                            </SectionCard>

                            <SectionCard section={sections[10]} delay={1.2}>
                                <p className="mb-4">
                                    We may update this Privacy Policy from time to time. The revised and most current version will be posted on our application.
                                </p>
                                <div className="bg-orange-50 border border-orange-200 p-4 rounded-xl">
                                    <p className="text-orange-800">
                                        <strong>Stay Updated:</strong> You are advised to review this Privacy Policy periodically for any changes. Changes become effective when posted.
                                    </p>
                                </div>
                            </SectionCard>

                            <SectionCard section={sections[11]} delay={1.3}>
                                <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl">
                                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                                        <Mail className="w-5 h-5 text-[#F05A28] mr-2" />
                                        Get in Touch
                                    </h4>
                                    <p className="text-gray-700 mb-4">
                                        At RoadExpress, we have a dedicated Privacy Officer to address any concerns or questions about your Personal Information or this Privacy Policy.
                                    </p>
                                    <div className="flex items-center">
                                        <Mail className="w-5 h-5 text-[#F05A28] mr-3" />
                                        <a href="mailto:connect@roadexpressfreight.com" className="text-[#F05A28] font-semibold hover:underline">
                                            connect@roadexpressfreight.com
                                        </a>
                                    </div>
                                </div>
                            </SectionCard>

                        </div>
                    </div>
                </div>

                {/* Download Button */}
                {/* <div className="fixed bottom-8 left-8">
                <button className="flex items-center bg-white text-[#F05A28] px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200">
                    <Download className="w-4 h-4 mr-2" />
                    <span className="font-medium">Download PDF</span>
                </button>
            </div> */}
            </div>
            <Footer />
        </>
    );
};

export default PrivacyPolicyPage;